import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface TelegramStatus {
  configured: boolean;
  channel_url: string | null;
}

interface SendRequest {
  post_id: number;
  // Omit / pass null to send the post's *original* source file via the
  // media proxy instead of a converted GIF/MP4.
  preset?: string | null;
}

interface SendResponse {
  queued: true;
  channel_url: string | null;
}

async function fetchStatus(): Promise<TelegramStatus> {
  const res = await fetch("/api/v1/telegram/status", {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`telegram status failed: ${res.status}`);
  return res.json();
}

async function send(body: SendRequest): Promise<SendResponse> {
  const res = await fetch("/api/v1/telegram/send", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `send failed: ${res.status}`);
  }
  return res.json();
}

export function useTelegramStatus() {
  return useQuery<TelegramStatus>({
    queryKey: ["telegram", "status"],
    queryFn: fetchStatus,
    // Value only changes when the operator rotates the secret + restarts.
    staleTime: 60 * 60 * 1000,
  });
}

export function useSendToTelegram() {
  return useMutation<SendResponse, Error, SendRequest>({
    mutationFn: send,
  });
}

// ── Per-user DM linking ────────────────────────────────────────────────────

export interface TelegramLinkStatus {
  linked: boolean;
}

interface LinkStartResponse {
  start_url: string;
  expires_in: number;
}

async function fetchLinkStatus(): Promise<TelegramLinkStatus> {
  const res = await fetch("/api/v1/telegram/link/status", {
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`link status failed: ${res.status}`);
  return res.json();
}

async function startLink(): Promise<LinkStartResponse> {
  const res = await fetch("/api/v1/telegram/link/start", {
    method: "POST",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `link start failed: ${res.status}`);
  }
  return res.json();
}

async function unlink(): Promise<void> {
  const res = await fetch("/api/v1/telegram/link", {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) throw new Error(`unlink failed: ${res.status}`);
}

async function sendDm(body: SendRequest): Promise<SendResponse> {
  const res = await fetch("/api/v1/telegram/dm/send", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res
      .json()
      .then((j) => (typeof j?.detail === "string" ? j.detail : null))
      .catch(() => null);
    throw new Error(detail ?? `dm send failed: ${res.status}`);
  }
  return res.json();
}

export function useTelegramLinkStatus(enabled = true) {
  return useQuery<TelegramLinkStatus>({
    queryKey: ["telegram", "link-status"],
    queryFn: fetchLinkStatus,
    enabled,
    // Re-checked when the user comes back to the tab after tapping Start
    // in Telegram — they expect to see "Linked" immediately.
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
}

export function useStartTelegramLink() {
  return useMutation<LinkStartResponse, Error, void>({
    mutationFn: startLink,
  });
}

export function useUnlinkTelegram() {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: unlink,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["telegram", "link-status"] }),
  });
}

export function useSendDmToTelegram() {
  return useMutation<SendResponse, Error, SendRequest>({
    mutationFn: sendDm,
  });
}
